import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getMyBusiness, updateDeal, updateMyBusiness, deleteDeal, verifyRedemptionCode, getRecentRedemptions, resendVerification, createCheckoutSession, openBillingPortal, getSubscriptionStatus, featureMyDeal } from '../api';
import './BusinessDashboardPage.css';

const CUISINES = ['canadian','indian','chinese','italian','mexican','japanese','thai','vietnamese','middle-eastern','greek','korean','american','seafood','pizza','sushi','vegetarian','vegan','breakfast','bakery','other'];
const HOODS = ['Downtown','St. Vital','River Heights','Transcona','North End','Osborne Village','Wolseley','West End','St. James','Elmwood','Fort Garry','Tuxedo'];
const MEAL_TYPES = ['Dine-in','Takeout','Delivery'];
const PRICE_RANGES = [{v:1,l:'$ — Budget friendly'},{v:2,l:'$$ — Moderate'},{v:3,l:'$$$ — Upscale'},{v:4,l:'$$$$ — Fine dining'}];

export default function BusinessDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('deals');
  const [subscription, setSubscription] = useState(null);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const upgradeSuccess = searchParams.get('upgrade') === 'success';
  const upgradedPlan   = searchParams.get('plan');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getMyBusiness(),
      getSubscriptionStatus().catch(() => null),
    ]).then(([bizData, subData]) => {
      setData(bizData);
      setSubscription(subData);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpgrade = async (plan) => {
    setUpgradingPlan(plan);
    try {
      const res = await createCheckoutSession(plan);
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert('Checkout error: ' + err.message);
    } finally {
      setUpgradingPlan(null);
    }
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await openBillingPortal();
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert('Billing portal error: ' + err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleDeal = async (deal) => {
    try {
      await updateDeal(deal.id, { is_active: !deal.is_active });
      load();
    } catch (err) {
      alert(err.message || 'Failed to update deal');
    }
  };

  const removeDeal = async (deal) => {
    if (!window.confirm(`Delete "${deal.title}"?\n\nIf customers have already claimed this deal, it will be deactivated (not fully deleted) to preserve their redemption codes.`)) return;
    try {
      const result = await deleteDeal(deal.id);
      if (result.softDeleted) {
        alert(`"${deal.title}" has been deactivated. Customers who already claimed it can still redeem their codes.`);
      }
      load();
    } catch (err) { alert('Error: ' + (err.message || 'Failed to delete deal')); }
  };

  const featureDeal = async (deal) => {
    try {
      const res = await featureMyDeal(deal.id);
      load();
      if (res.featured) {
        alert(`✓ "${deal.title}" is now your Deal of the Day on halfprice.ca!`);
      }
    } catch (err) {
      alert(err.message || 'Failed to update featured deal');
    }
  };

  if (loading) return (
    <div className="dashboard-loading">
      <div className="skeleton" style={{ height: 120, borderRadius: 16, marginBottom: 16 }} />
      <div className="skeleton" style={{ height: 300, borderRadius: 16 }} />
    </div>
  );

  if (!data) return <p style={{ padding: 40 }}>Failed to load dashboard.</p>;

  const { business, deals, stats } = data;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="container">
          <div className="dash-header-inner">
            <div>
              <h1 className="dash-title">{business.business_name}</h1>
              <p className="dash-sub">{business.cuisine_type} · {business.neighbourhood || 'Winnipeg'}</p>
            </div>
            <div className="dash-header-actions">
              <span className={`badge ${business.is_verified ? 'badge-success' : 'badge-muted'}`}>
                {business.is_verified ? '✓ Verified' : 'Pending verification'}
              </span>
              <span className="badge badge-brand">{business.plan} plan</span>
              <Link to="/business/deals/new" className="btn btn-primary btn-sm">+ New deal</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container dashboard-body">
        {!business.is_verified && <VerificationBanner email={business.email} />}

        {/* Upgrade success banner */}
        {upgradeSuccess && (
          <div className="upgrade-success-banner">
            <span className="usb-icon">🎉</span>
            <div>
              <strong>You've upgraded to the {upgradedPlan ? upgradedPlan.charAt(0).toUpperCase()+upgradedPlan.slice(1) : ''} plan!</strong>
              <p>Your new deal limits and features are active immediately.</p>
            </div>
          </div>
        )}

        {/* Cancellation pending banner */}
        {subscription?.subscription?.cancel_at_period_end && subscription?.subscription?.current_period_end && (
          <div className="plan-cancellation-banner">
            <span style={{fontSize:22}}>⚠️</span>
            <div>
              <strong>Your {business.plan.charAt(0).toUpperCase()+business.plan.slice(1)} plan is scheduled to end on {new Date(subscription.subscription.current_period_end).toLocaleDateString('en-CA', { month:'long', day:'numeric', year:'numeric' })}.</strong>
              <p>Your active deals will remain live until that date. After that your account reverts to the Free plan (1 active deal). You can resubscribe anytime via Manage billing.</p>
            </div>
            <button className="btn btn-outline btn-sm" style={{whiteSpace:'nowrap',flexShrink:0}} onClick={handleBillingPortal} disabled={portalLoading}>
              {portalLoading ? 'Opening…' : 'Resubscribe'}
            </button>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-num">{stats.active_deals || 0}</div>
            <div className="stat-card-label">Active deals</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num">{Number(stats.total_views || 0).toLocaleString()}</div>
            <div className="stat-card-label">Total views</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num">{stats.total_redemptions || 0}</div>
            <div className="stat-card-label">Redemptions</div>
          </div>
          <PlanStatCard business={business} subscription={subscription} onUpgrade={handleUpgrade} onPortal={handleBillingPortal} upgradingPlan={upgradingPlan} portalLoading={portalLoading} />
        </div>

        <PlanUsageBar business={business} deals={deals} onUpgrade={handleUpgrade} upgradingPlan={upgradingPlan} />

        <div className="dash-tabs">
          <button className={`dash-tab ${tab === 'deals' ? 'active' : ''}`} onClick={() => setTab('deals')}>My deals ({deals.length})</button>
          <button className={`dash-tab ${tab === 'verify' ? 'active' : ''}`} onClick={() => setTab('verify')}>Verify code</button>
          {(business.plan === 'starter' || business.plan === 'pro') && (
            <button className={`dash-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>Analytics</button>
          )}
          <button className={`dash-tab ${tab === 'profile' ? 'active' : ''}`} onClick={() => setTab('profile')}>Business profile</button>
        </div>

        {tab === 'deals' && (
          <div className="deals-table-section">
            {/* Pro plan: Deal of the Day info card */}
            {business.plan === 'pro' && (
              <div className="pro-featured-info">
                <div className="pfi-left">
                  <span className="pfi-icon">⭐</span>
                  <div>
                    <strong>Deal of the Day — included with Pro</strong>
                    <p>Pick one deal to appear as the homepage hero on halfprice.ca. Click <strong>☆ Set as Deal of the Day</strong> on any active deal below. Only one deal can be featured at a time.</p>
                    <p className="pfi-note">
                      Want guaranteed exclusive placement for a full week? Contact us about a
                      <a href="mailto:hello@halfprice.ca?subject=Featured Slot Inquiry"> Featured Slot ($99–199/week) →</a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {deals.length === 0 ? (
              <div className="empty-deals">
                <p style={{ fontSize: 40, marginBottom: 12 }}>🏷</p>
                <h3>No deals yet</h3>
                <p>Create your first deal and start attracting customers today.</p>
                <Link to="/business/deals/new" className="btn btn-primary mt-2">Create your first deal →</Link>
              </div>
            ) : (
              <div className="deals-table-wrap">
                <table className="deals-table">
                  <thead>
                    <tr>
                      <th>Deal</th><th>Price</th><th>Discount</th><th>Views</th><th>Claimed</th><th>Expires</th><th>Status</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map(d => (
                      <tr key={d.id} className={d.is_featured ? 'deal-row-featured' : ''}>
                        <td className="deal-row-title">
                          <span>{d.title}</span>
                          {d.is_featured && <span className="badge badge-brand" style={{ marginLeft: 8, fontSize: 10 }}>⭐ Deal of the Day</span>}
                        </td>
                        <td>
                          <span className="deal-row-price">${Number(d.deal_price).toFixed(2)}</span>
                          <span className="deal-row-orig">${Number(d.original_price).toFixed(2)}</span>
                        </td>
                        <td><span className="badge badge-brand">{d.discount_percent}% off</span></td>
                        <td>{d.views}</td>
                        <td>{d.current_redemptions}</td>
                        <td>{new Date(d.valid_until).toLocaleDateString('en-CA')}</td>
                        <td>
                          <button className={`toggle-btn ${d.is_active ? 'active' : 'inactive'}`} onClick={() => toggleDeal(d)}>
                            {d.is_active ? 'Active' : 'Paused'}
                          </button>
                        </td>
                        <td>
                          <div className="deal-row-actions">
                            {business.plan === 'pro' && (
                              <button
                                className={`deal-feature-btn ${d.is_featured ? 'featured' : ''}`}
                                title={d.is_featured ? 'Remove from Deal of the Day' : 'Set as Deal of the Day on homepage'}
                                onClick={() => featureDeal(d)}
                              >
                                {d.is_featured ? '⭐' : '☆'}
                              </button>
                            )}
                            <Link to={`/business/deals/edit/${d.id}`} className="btn btn-ghost btn-sm">Edit</Link>
                            <button className="delete-btn" onClick={() => removeDeal(d)} title="Delete deal">🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'verify' && <VerifyCodeTab />}
        {tab === 'analytics' && <AnalyticsTab deals={deals} stats={stats} />}
        {tab === 'profile' && <ProfileTab business={business} onSaved={load} />}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────
const PLAN_CONFIG = {
  free:    { label:'Free',    price:'$0',   color:'var(--text-muted)',  next:'starter' },
  starter: { label:'Starter', price:'$49',  color:'var(--brand)',       next:'pro'     },
  pro:     { label:'Pro',     price:'$99',  color:'var(--success)',     next:null      },
};

function PlanStatCard({ business, subscription, onUpgrade, onPortal, upgradingPlan, portalLoading }) {
  const plan   = business.plan || 'free';
  const cfg    = PLAN_CONFIG[plan];
  const isPaid = plan !== 'free';
  const sub    = subscription?.subscription;
  const expiry = sub?.current_period_end;
  const cancelAtPeriodEnd = sub?.cancel_at_period_end;

  return (
    <div className="stat-card stat-card-plan">
      <div className="stat-card-num" style={{ color: cfg.color, fontSize: 20 }}>
        {cfg.label} plan
      </div>
      <div className="stat-card-label">{cfg.price}{isPaid ? '/month CAD' : ' forever'}</div>

      {/* Show renewal date OR cancellation warning */}
      {cancelAtPeriodEnd && expiry ? (
        <div className="plan-cancelling">
          Ends {new Date(expiry).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      ) : expiry ? (
        <div className="plan-renews">
          Renews {new Date(expiry).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
        </div>
      ) : null}

      {/* Free plan — show BOTH Starter and Pro buttons */}
      {plan === 'free' && (
        <div className="plan-upgrade-both">
          <button className="btn btn-primary btn-sm" style={{ width:'100%', justifyContent:'center' }}
            disabled={upgradingPlan === 'starter'} onClick={() => onUpgrade('starter')}>
            {upgradingPlan === 'starter' ? 'Redirecting…' : 'Starter — $49/mo →'}
          </button>
          <button className="btn btn-outline btn-sm" style={{ width:'100%', justifyContent:'center' }}
            disabled={upgradingPlan === 'pro'} onClick={() => onUpgrade('pro')}>
            {upgradingPlan === 'pro' ? 'Redirecting…' : 'Pro — $99/mo →'}
          </button>
        </div>
      )}

      {/* Starter plan — upgrade to Pro + Manage Billing */}
      {plan === 'starter' && (
        <div className="plan-upgrade-both">
          <button className="btn btn-primary btn-sm" style={{ width:'100%', justifyContent:'center' }}
            disabled={upgradingPlan === 'pro'} onClick={() => onUpgrade('pro')}>
            {upgradingPlan === 'pro' ? 'Redirecting…' : 'Upgrade to Pro →'}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ width:'100%', justifyContent:'center' }}
            disabled={portalLoading} onClick={onPortal}>
            {portalLoading ? 'Opening…' : 'Manage billing'}
          </button>
        </div>
      )}

      {/* Pro plan — Manage Billing only */}
      {plan === 'pro' && (
        <button className="btn btn-ghost btn-sm mt-1" style={{ width:'100%', justifyContent:'center' }}
          disabled={portalLoading} onClick={onPortal}>
          {portalLoading ? 'Opening…' : 'Manage billing'}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────
function PlanUsageBar({ business, deals, onUpgrade, upgradingPlan }) {
  const LIMITS = { free: 1, starter: 5, pro: 999 };
  const plan = business.plan || 'free';
  const limit = LIMITS[plan];
  const activeCount = deals.filter(d => d.is_active).length;
  const atLimit = activeCount >= limit && plan !== 'pro';
  const pct = plan === 'pro' ? 100 : Math.min(100, Math.round((activeCount / limit) * 100));

  return (
    <div className={`plan-usage-bar ${atLimit ? 'at-limit' : ''}`}>
      <div className="plan-usage-left">
        <span className="plan-usage-label">
          <strong>{plan.charAt(0).toUpperCase() + plan.slice(1)} plan</strong>
          {plan !== 'pro'
            ? ` · ${activeCount} of ${limit} active deal${limit !== 1 ? 's' : ''} used`
            : ` · Unlimited active deals`}
        </span>
        {plan !== 'pro' && (
          <div className="plan-usage-track">
            <div className="plan-usage-fill" style={{ width: `${pct}%`, background: atLimit ? 'var(--brand)' : 'var(--success)' }} />
          </div>
        )}
        {atLimit && (
          <span className="plan-usage-warning">
            ⚠ Deal limit reached — pause an existing deal or upgrade to add more.
          </span>
        )}
      </div>
      {/* Free plan: show both Starter and Pro */}
      {plan === 'free' && (
        <div className="plan-usage-actions">
          <button className="btn btn-primary btn-sm" disabled={upgradingPlan === 'starter'} onClick={() => onUpgrade('starter')}>
            {upgradingPlan === 'starter' ? 'Redirecting…' : 'Starter $49/mo'}
          </button>
          <button className="btn btn-outline btn-sm" disabled={upgradingPlan === 'pro'} onClick={() => onUpgrade('pro')}>
            {upgradingPlan === 'pro' ? 'Redirecting…' : 'Pro $99/mo'}
          </button>
        </div>
      )}
      {/* Starter plan: show upgrade to Pro */}
      {plan === 'starter' && (
        <button className="btn btn-outline btn-sm" style={{ whiteSpace:'nowrap' }}
          disabled={upgradingPlan === 'pro'} onClick={() => onUpgrade('pro')}>
          {upgradingPlan === 'pro' ? 'Redirecting…' : 'Upgrade to Pro'}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────
function VerificationBanner({ email }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const resend = async () => {
    setSending(true);
    try { await resendVerification(email); setSent(true); } catch {} finally { setSending(false); }
  };

  return (
    <div className="verify-banner">
      <div>
        <strong>Verify your email to activate your restaurant.</strong>
        <p>We sent a verification link to {email}. Click it to go live and appear in search results.</p>
      </div>
      {sent ? (
        <span className="badge badge-success">Email sent ✓</span>
      ) : (
        <button className="btn btn-outline btn-sm" onClick={resend} disabled={sending}>
          {sending ? 'Sending…' : 'Resend email'}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────
function AnalyticsTab({ deals, stats }) {
  const totalViews       = deals.reduce((s, d) => s + (d.views || 0), 0);
  const totalRedemptions = deals.reduce((s, d) => s + (d.current_redemptions || 0), 0);
  const conversionRate   = totalViews > 0 ? ((totalRedemptions / totalViews) * 100).toFixed(1) : '0.0';
  const bestDeal         = deals.reduce((best, d) => (!best || d.current_redemptions > best.current_redemptions) ? d : best, null);
  const mostViewed       = deals.reduce((best, d) => (!best || d.views > best.views) ? d : best, null);
  const activeDeals      = deals.filter(d => d.is_active && new Date(d.valid_until) > new Date());
  const expiringSoon     = activeDeals.filter(d => {
    const daysLeft = Math.ceil((new Date(d.valid_until) - new Date()) / 86400000);
    return daysLeft <= 7;
  });

  return (
    <div className="analytics-tab">
      <div className="analytics-grid">
        <div className="analytics-card">
          <div className="ac-value">{totalViews.toLocaleString()}</div>
          <div className="ac-label">Total deal views</div>
          <div className="ac-sub">All time across all deals</div>
        </div>
        <div className="analytics-card">
          <div className="ac-value">{totalRedemptions}</div>
          <div className="ac-label">Total claims</div>
          <div className="ac-sub">Customers who claimed a code</div>
        </div>
        <div className="analytics-card">
          <div className="ac-value">{conversionRate}%</div>
          <div className="ac-label">Conversion rate</div>
          <div className="ac-sub">Views → claims ratio</div>
        </div>
        <div className="analytics-card">
          <div className="ac-value">{activeDeals.length}</div>
          <div className="ac-label">Active deals</div>
          <div className="ac-sub">Currently live on site</div>
        </div>
      </div>

      <div className="analytics-row">
        {bestDeal && (
          <div className="analytics-highlight">
            <div className="ah-label">🏆 Best performing deal</div>
            <div className="ah-title">{bestDeal.title}</div>
            <div className="ah-stats">
              <span>{bestDeal.current_redemptions} claims</span>
              <span>{bestDeal.views} views</span>
              <span>{bestDeal.discount_percent}% off</span>
            </div>
          </div>
        )}
        {mostViewed && mostViewed.id !== bestDeal?.id && (
          <div className="analytics-highlight">
            <div className="ah-label">👀 Most viewed deal</div>
            <div className="ah-title">{mostViewed.title}</div>
            <div className="ah-stats">
              <span>{mostViewed.views} views</span>
              <span>{mostViewed.current_redemptions} claims</span>
              <span>{mostViewed.discount_percent}% off</span>
            </div>
          </div>
        )}
      </div>

      {expiringSoon.length > 0 && (
        <div className="analytics-expiring">
          <div className="ae-label">⚠ Expiring soon (within 7 days)</div>
          {expiringSoon.map(d => {
            const days = Math.ceil((new Date(d.valid_until) - new Date()) / 86400000);
            return (
              <div key={d.id} className="ae-row">
                <span>{d.title}</span>
                <span className="ae-days">{days === 1 ? 'Last day!' : `${days} days left`}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="analytics-deals-table">
        <div className="adt-label">All deals performance</div>
        <table className="deals-analytics-table">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Views</th>
              <th>Claims</th>
              <th>Rate</th>
              <th>Discount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {deals.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign:'center', color:'var(--text-muted)', padding:24 }}>No deals yet. Create your first deal to see analytics.</td></tr>
            )}
            {deals.map(d => {
              const rate = d.views > 0 ? ((d.current_redemptions / d.views) * 100).toFixed(1) : '0.0';
              const isExpired = new Date(d.valid_until) < new Date();
              return (
                <tr key={d.id}>
                  <td style={{ fontSize:13 }}>{d.title}</td>
                  <td style={{ textAlign:'center' }}>{d.views}</td>
                  <td style={{ textAlign:'center' }}>{d.current_redemptions}</td>
                  <td style={{ textAlign:'center' }}>
                    <span style={{ color: parseFloat(rate) > 5 ? 'var(--success)' : 'var(--text-muted)', fontWeight:500 }}>
                      {rate}%
                    </span>
                  </td>
                  <td style={{ textAlign:'center' }}>
                    <span className="badge badge-brand">{d.discount_percent}%</span>
                  </td>
                  <td>
                    {isExpired
                      ? <span className="badge badge-muted">Expired</span>
                      : d.is_active
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-muted">Paused</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────
function VerifyCodeTab() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => getRecentRedemptions().then(setRecent).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true); setResult(null);
    try {
      const res = await verifyRedemptionCode(code.trim());
      setResult(res);
      setCode('');
      loadRecent();
    } catch (err) {
      setResult({ valid: false, message: err.message || 'Verification failed' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="verify-tab">
      <div className="verify-box">
        <h3>Verify a customer's code</h3>
        <p className="verify-sub">Ask the customer for their redemption code (or loyalty bonus code) and enter it below to validate and mark it as used.</p>
        <form onSubmit={handleVerify} className="verify-form">
          <input
            className="form-control verify-input"
            placeholder="e.g. HP-AB12CD or LOYAL-XY789"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            autoFocus
          />
          <button type="submit" className="btn btn-primary" disabled={checking}>
            {checking ? 'Checking…' : 'Verify code'}
          </button>
        </form>

        {result && (
          <div className={`verify-result ${result.valid ? 'valid' : 'invalid'}`}>
            <span className="verify-result-icon">{result.valid ? '✓' : '✗'}</span>
            <div>
              <strong>{result.valid ? 'Valid — redeemed!' : 'Not valid'}</strong>
              <p>{result.message}</p>
              {result.consumer_email && <p className="verify-email">Customer: {result.consumer_email}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="loyalty-explainer">
        <div className="loyalty-explainer-header">🎁 How loyalty stamps work</div>
        <p>Every time a customer claims one of your deals, they earn <strong>+1 stamp</strong> linked to your restaurant. After <strong>3 visits</strong>, they receive a special <strong>LOYAL-XXXXX</strong> bonus code.</p>
        <p>When a customer shows you their bonus code, you decide the reward — a <strong>free dessert, free drink, 10% off</strong>, anything you like. It costs you almost nothing and turns one-time deal hunters into regular customers.</p>
        <p className="loyalty-note">💡 Tip: Decide your bonus reward before you launch your first deal and train your staff. Example: <em>"Show us your LOYAL code and get a free dessert."</em></p>
      </div>

      <div className="recent-redemptions">
        <h3>Recent redemptions</h3>
        {recent.length === 0 ? (
          <p className="empty-hint">No redemptions yet. When customers claim your deals, codes will appear here.</p>
        ) : (
          <div className="redemptions-list">
            {recent.map((r, i) => (
              <div key={i} className="redemption-row">
                <div>
                  <strong>{r.redemption_code}</strong>
                  <span className="redemption-deal">{r.deal_title}</span>
                </div>
                <div className="redemption-meta">
                  <span>{r.consumer_email}</span>
                  <span className={`badge ${r.status === 'used' ? 'badge-success' : 'badge-muted'}`}>
                    {r.status === 'used' ? 'Redeemed' : 'Not yet used'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────
function ProfileTab({ business, onSaved }) {
  const [form, setForm] = useState({
    business_name: business.business_name || '',
    owner_name: business.owner_name || '',
    phone: business.phone || '',
    address: business.address || '',
    neighbourhood: business.neighbourhood || '',
    cuisine_type: business.cuisine_type || '',
    price_range: business.price_range || 2,
    meal_types: business.meal_types || [],
    description: business.description || '',
    website: business.website || '',
    instagram_handle: business.instagram_handle || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const setF = (k, v) => { setForm(p => ({ ...p, [k]: v })); setSaved(false); };
  const toggleMealType = (t) => setF('meal_types', form.meal_types.includes(t) ? form.meal_types.filter(x=>x!==t) : [...form.meal_types, t]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      await updateMyBusiness(form);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-section">
      {error && <div className="auth-error">{error}</div>}
      {saved && <div className="save-success">✓ Profile updated successfully</div>}
      <form onSubmit={handleSave}>
        <div className="form-row-2">
          <div className="form-group">
            <label>Restaurant name</label>
            <input className="form-control" value={form.business_name} onChange={e=>setF('business_name', e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Owner / manager name</label>
            <input className="form-control" value={form.owner_name} onChange={e=>setF('owner_name', e.target.value)} required />
          </div>
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>Cuisine type</label>
            <select className="form-control" value={form.cuisine_type} onChange={e=>setF('cuisine_type', e.target.value)}>
              {CUISINES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Price range</label>
            <select className="form-control" value={form.price_range} onChange={e=>setF('price_range', Number(e.target.value))}>
              {PRICE_RANGES.map(p=><option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Meal types offered</label>
          <div className="meal-type-row">
            {MEAL_TYPES.map(t=>(
              <button type="button" key={t} className={`meal-type-btn ${form.meal_types.includes(t)?'active':''}`} onClick={()=>toggleMealType(t)}>{t}</button>
            ))}
          </div>
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>Neighbourhood</label>
            <select className="form-control" value={form.neighbourhood} onChange={e=>setF('neighbourhood', e.target.value)}>
              <option value="">Select…</option>
              {HOODS.map(h=><option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input className="form-control" value={form.phone} onChange={e=>setF('phone', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>Address</label>
          <input className="form-control" value={form.address} onChange={e=>setF('address', e.target.value)} />
        </div>
        <div className="form-group">
          <label>About your restaurant</label>
          <textarea className="form-control" rows={3} value={form.description} onChange={e=>setF('description', e.target.value)} />
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>Website</label>
            <input className="form-control" value={form.website} onChange={e=>setF('website', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Instagram handle</label>
            <input className="form-control" value={form.instagram_handle} onChange={e=>setF('instagram_handle', e.target.value.replace('@',''))} />
          </div>
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>Email (cannot be changed)</label>
            <input className="form-control" value={business.email} disabled style={{opacity:0.6}} />
          </div>
          <div className="form-group">
            <label>Member since</label>
            <input className="form-control" value={new Date(business.created_at).toLocaleDateString('en-CA',{month:'long',year:'numeric'})} disabled style={{opacity:0.6}} />
          </div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </div>
  );
}
